import * as React from "react";

/**
 * The small burst that leaves the confirm button the moment it is pressed (#820).
 *
 * Pure decoration: `aria-hidden`, no pointer events, and drawn only for people who
 * have not asked for reduced motion (`motion-safe:`), so with it off the particles
 * are not shown at all. Each particle flies along its own vector, set as custom
 * properties the `confirm-confetti` keyframes read, so the burst needs no JS timers
 * and unmounts with the button it belongs to.
 *
 * One colour on purpose: the success green of the button itself. In this product a
 * multi-colour gradient means "the AI did this" (DESIGN.md), so a colourful burst
 * would tell the agent something false. Keep it a single hue.
 */

const COLOR = "var(--success)";

type Particle = { dx: number; dy: number; rot: number; size: number; delay: number };

// Fixed, not random: the same burst every time, so it renders identically on the server and
// in tests. Few, small and close: a flourish at the button, not a celebration across the card.
const PARTICLES: Particle[] = Array.from({ length: 8 }, (_, i) => {
  const angle = (i / 8) * Math.PI * 2 + (i % 2 ? 0.2 : -0.1);
  const reach = i % 2 ? 24 : 32;
  return {
    dx: Math.round(Math.cos(angle) * reach),
    // Biased upward: it lifts a little before it settles.
    dy: Math.round(Math.sin(angle) * reach - 6),
    rot: (i % 2 ? 1 : -1) * (90 + i * 20),
    size: i % 2 ? 4 : 5,
    delay: (i % 4) * 20,
  };
});

export function ConfirmBurst() {
  return (
    <span aria-hidden data-confirm-burst className="pointer-events-none absolute inset-0 z-10">
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="absolute left-1/2 top-1/2 hidden rounded-[2px] motion-safe:block motion-safe:animate-confirm-confetti"
          style={
            {
              width: p.size,
              height: p.size,
              marginLeft: -p.size / 2,
              marginTop: -p.size / 2,
              background: COLOR,
              animationDelay: `${p.delay}ms`,
              "--dx": `${p.dx}px`,
              "--dy": `${p.dy}px`,
              "--rot": `${p.rot}deg`,
            } as React.CSSProperties
          }
        />
      ))}
    </span>
  );
}
